import { useEffect, useMemo, useRef, useState } from "react";
import type { DispatchRecord, ScheduledPost } from "../api";
import type { NavKey } from "./Sidebar";
import {
  IconAnalytics,
  IconCalendar,
  IconChannels,
  IconContent,
  IconPlus,
  IconSearch,
  IconSettings,
} from "../lib/icons";

interface Command {
  id: string;
  group: string;
  label: string;
  sub?: string;
  icon: React.ReactNode;
  run: () => void;
  /** Extra text folded into the fuzzy match (e.g. a post's platforms). */
  keywords?: string;
}

/** Case-insensitive subsequence match — "fp" matches "Failed Publishes". */
function fuzzy(query: string, text: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  let i = 0;
  for (const ch of t) {
    if (ch === q[i]) i++;
    if (i === q.length) return true;
  }
  return q.length === 0;
}

/**
 * The ⌘K / Ctrl+K command palette: a search + action overlay. Actions cover
 * Create post, navigate to any section, open a channel/settings, and jump to
 * failed publishes; a "Find a post" fuzzy search spans recent + scheduled post
 * titles. Accessible: focus moves to the input, ↑/↓ move the selection, Enter
 * runs it, Esc closes.
 */
export function CommandPalette({
  open,
  onClose,
  scheduled,
  history,
  onCreate,
  onNavigate,
  onSettings,
  onViewFailures,
  onEditPost,
}: {
  open: boolean;
  onClose: () => void;
  scheduled: ScheduledPost[];
  history: DispatchRecord[];
  onCreate: () => void;
  onNavigate: (nav: NavKey) => void;
  onSettings: () => void;
  onViewFailures: () => void;
  onEditPost: (post: ScheduledPost) => void;
}) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Reset the query + selection each time the palette opens, and focus the input.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      // Focus after paint so the autoFocus race with the overlay mount is avoided.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const commands = useMemo<Command[]>(() => {
    const wrap = (fn: () => void) => () => {
      onClose();
      fn();
    };
    const actions: Command[] = [
      { id: "create", group: "Actions", label: "Create post", sub: "N", icon: <IconPlus size={17} />, run: wrap(onCreate) },
      { id: "failed", group: "Actions", label: "View failed publishes", icon: <IconAnalytics size={17} />, run: wrap(onViewFailures) },
      { id: "nav-calendar", group: "Go to", label: "Dashboard", icon: <IconContent size={17} />, run: wrap(() => onNavigate("calendar")) },
      { id: "nav-planner", group: "Go to", label: "Calendar", icon: <IconCalendar size={17} />, run: wrap(() => onNavigate("planner")) },
      { id: "nav-connections", group: "Go to", label: "Connections", icon: <IconChannels size={17} />, run: wrap(() => onNavigate("connections")) },
      { id: "nav-analytics", group: "Go to", label: "Analytics", icon: <IconAnalytics size={17} />, run: wrap(() => onNavigate("analytics")) },
      { id: "settings", group: "Go to", label: "Settings", sub: "Billing, notifications, security", icon: <IconSettings size={17} />, run: wrap(onSettings) },
    ];

    // Find a post: scheduled titles are editable; recent published titles jump
    // to the calendar where their detail lives.
    const posts: Command[] = [];
    for (const p of scheduled.slice(0, 30)) {
      posts.push({
        id: `sched-${p.id}`,
        group: "Find a post",
        label: p.draft.title || "Untitled post",
        sub: "Scheduled",
        keywords: p.platforms.join(" "),
        icon: <IconCalendar size={17} />,
        run: wrap(() => onEditPost(p)),
      });
    }
    for (const r of [...history].sort((a, b) => b.at - a.at).slice(0, 20)) {
      posts.push({
        id: `hist-${r.id}`,
        group: "Find a post",
        label: r.title || "Untitled post",
        sub: "Published",
        icon: <IconCalendar size={17} />,
        run: wrap(() => onNavigate("calendar")),
      });
    }

    return [...actions, ...posts];
  }, [scheduled, history, onCreate, onNavigate, onSettings, onViewFailures, onEditPost, onClose]);

  const filtered = useMemo(
    () => commands.filter((c) => fuzzy(query, `${c.label} ${c.group} ${c.keywords ?? ""}`)),
    [commands, query],
  );

  // Keep the active index in range as the filtered list changes.
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      filtered[active]?.run();
    }
  };

  // Render grouped, but keep a single flat index for keyboard navigation.
  let flatIndex = -1;
  let lastGroup = "";

  return (
    <div
      className="cc-palette-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cc-palette" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="cc-palette-search">
          <span className="cc-palette-ico" aria-hidden="true">
            <IconSearch size={17} />
          </span>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search actions, posts, channels…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            aria-label="Command palette search"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="cc-palette-empty">No matches for “{query}”.</div>
        ) : (
          <ul className="cc-palette-list" ref={listRef}>
            {filtered.map((c) => {
              flatIndex += 1;
              const idx = flatIndex;
              const showGroup = c.group !== lastGroup;
              lastGroup = c.group;
              return (
                <li key={c.id}>
                  {showGroup && <div className="cc-palette-group">{c.group}</div>}
                  <button
                    type="button"
                    className={`cc-palette-item${idx === active ? " active" : ""}`}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => c.run()}
                  >
                    <span className="cc-palette-ico">{c.icon}</span>
                    <span>{c.label}</span>
                    {c.sub && <span className="cc-palette-sub">{c.sub}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
